import QRCodeStyling from "qr-code-styling";
import { jsPDF } from "jspdf";
import { fileUrl, download } from "./api";

export const DEFAULT_DESIGN = {
  fg: "#111111", bg: "#FFFFFF", gradient: false, fg2: "#3B82F6", dot: "rounded", eye: "extra-rounded", eye_colour: "#111111",
  logo: null, frame: false, frame_text: "Scan me", frame_colour: "#111111", ecl: "M",
};

export function staticPayload(type, d = {}) {
  switch (type) {
    case "wifi": return `WIFI:T:${d.encryption || "WPA"};S:${(d.ssid || "").replace(/([;,:\\"])/g, "\\$1")};P:${(d.password || "").replace(/([;,:\\"])/g, "\\$1")};${d.hidden ? "H:true;" : ""};`;
    case "vcard": return ["BEGIN:VCARD", "VERSION:3.0", `N:${d.last_name || ""};${d.first_name || ""}`, `FN:${d.first_name || ""} ${d.last_name || ""}`, d.company && `ORG:${d.company}`, d.phone && `TEL:${d.phone}`, d.email && `EMAIL:${d.email}`, d.website && `URL:${d.website}`, "END:VCARD"].filter(Boolean).join("\n");
    case "email": return `mailto:${d.to || ""}?subject=${encodeURIComponent(d.subject || "")}&body=${encodeURIComponent(d.body || "")}`;
    case "sms": return `SMSTO:${d.number || ""}:${d.message || ""}`;
    default: return d.text || "";
  }
}

const lum = (hex) => {
  const c = (hex || "#000").replace("#", "").match(/.{2}/g).map((x) => parseInt(x, 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};
export const contrast = (a, b) => { const [x, y] = [lum(a), lum(b)].sort((m, n) => n - m); return (x + 0.05) / (y + 0.05); };

export function makeQR(data, d, size = 300) {
  d = { ...DEFAULT_DESIGN, ...d };
  const dots = d.gradient ? { type: d.dot, gradient: { type: "linear", rotation: 0.8, colorStops: [{ offset: 0, color: d.fg }, { offset: 1, color: d.fg2 }] } } : { type: d.dot, color: d.fg };
  return new QRCodeStyling({
    width: size, height: size, type: "svg", data: data || " ", margin: 8, image: d.logo ? fileUrl(d.logo) : undefined,
    qrOptions: { errorCorrectionLevel: d.logo && d.ecl === "L" ? "M" : d.ecl },
    dotsOptions: dots, backgroundOptions: { color: d.bg },
    cornersSquareOptions: { type: d.eye, color: d.eye_colour }, cornersDotOptions: { type: d.eye === "square" ? "square" : "dot", color: d.eye_colour },
    imageOptions: { crossOrigin: "anonymous", margin: 6, imageSize: 0.3, hideBackgroundDots: true },
  });
}

const escXml = (s) => s.replace(/[<>&"']/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;" }[c]));

export async function framedSvg(data, d, size = 1024) {
  const qr = makeQR(data, d, size);
  const raw = await (await qr.getRawData("svg")).text();
  const inner = raw.replace(/<\?xml[^>]*>/, "");
  if (!d.frame) return inner;
  const pad = Math.round(size * 0.06), textH = Math.round(size * 0.16), W = size + pad * 2, H = size + pad * 2 + textH;
  const nested = inner.replace("<svg", `<svg x="${pad}" y="${pad}" width="${size}" height="${size}"`);
  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">`
    + `<rect width="${W}" height="${H}" rx="${pad}" fill="${d.frame_colour}"/><rect x="${pad / 2}" y="${pad / 2}" width="${size + pad}" height="${size + pad}" rx="${pad / 2}" fill="${d.bg}"/>${nested}`
    + `<text x="${W / 2}" y="${size + pad * 2 + textH * 0.62}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="700" font-size="${Math.round(textH * 0.5)}" fill="#FFFFFF">${escXml(d.frame_text || "")}</text></svg>`;
}

async function svgToPng(svg) {
  const img = new Image();
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
  const c = document.createElement("canvas");
  c.width = img.width * 2; c.height = img.height * 2;
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  URL.revokeObjectURL(url);
  return c;
}

export async function exportQR(data, d, format, name) {
  const svg = await framedSvg(data, d, 1024);
  if (format === "svg") return download(new Blob([svg], { type: "image/svg+xml" }), `${name}.svg`);
  const canvas = await svgToPng(svg);
  if (format === "png") return canvas.toBlob((b) => download(b, `${name}.png`), "image/png");
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const w = 120, h = (canvas.height / canvas.width) * w;
  pdf.addImage(canvas.toDataURL("image/png"), "PNG", (210 - w) / 2, 40, w, h);
  pdf.save(`${name}.pdf`);
}
